import type { List } from 'immutable';

/**
 * Pad bounds if they are too close to each other
 * @param bounds {google.maps.LatLngBounds}
 * @param minDistance {number}
 */
export const padBoundsIfTooClose = (
  bounds: google.maps.LatLngBounds,
  minDistance = 0.0015
): google.maps.LatLngBounds => {
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();
  const latDiff = northEast.lat() - southWest.lat();
  const lngDiff = northEast.lng() - southWest.lng();

  if (Math.max(latDiff, lngDiff) < minDistance) {
    return bounds
      .extend(new google.maps.LatLng(northEast.lat() + minDistance, northEast.lng() + minDistance))
      .extend(new google.maps.LatLng(southWest.lat() - minDistance, southWest.lng() - minDistance));
  }
  return bounds;
};

export const isEmpty = (array: List<any> | any[]) => {
  if (!array) {
    return true;
  }

  return Array.isArray(array) ? array.length === 0 : array.size === 0;
};

// Small generic helper kept here for test visibility (previously inline)
export const shallowArrayEqual = <T>(
  a?: ReadonlyArray<T>,
  b?: ReadonlyArray<T>,
  cmp?: (x: T, y: T) => boolean
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  const compare = cmp || ((x: T, y: T) => x === y);
  for (let i = 0; i < a.length; i++) {
    if (!compare(a[i], b[i])) {
      return false;
    }
  }
  return true;
};

export const createThrottle = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  wait: number
) => {
  let lastExec = 0;
  let trailingArgs: Args | null = null;
  let trailingTimeout: ReturnType<typeof setTimeout> | null = null;
  const execute = () => {
    if (trailingArgs) {
      fn(...trailingArgs);
      trailingArgs = null;
      lastExec = Date.now();
    }
    if (trailingTimeout) {
      clearTimeout(trailingTimeout);
      trailingTimeout = null;
    }
  };
  const throttled = (...args: Args) => {
    trailingArgs = args;
    const now = Date.now();
    const elapsed = now - lastExec;
    if (elapsed >= wait) {
      execute();
    } else if (trailingTimeout == null) {
      const remaining = wait - elapsed;
      trailingTimeout = setTimeout(execute, remaining);
    }
  };
  throttled.flush = () => execute();
  return throttled as ((...args: Args) => void) & { flush: () => void };
};
